<template>
  <b-modal
    centered
    size="xl"
    :visible="show"
    hide-footer
    hide-header
    no-close-on-backdrop
    no-close-on-esc
  >
    <b-container fluid>
      <b-row>
        <b-col>
          <h4 class="text-info">{{ $t("globals.add_devices_from_stock") }}:</h4>
        </b-col>
      </b-row>
      <!-- Search Row -->
      <b-row>
        <b-col>
          <b-input-group size="sm" class="my-1">
            <b-input-group-prepend is-text>
              <v-icon name="hdd" :spin="searching_imei"></v-icon>
            </b-input-group-prepend>
            <b-form-input
              placeholder="IMEI"
              v-model="search_imei"
              @keyup.enter="searchByImei"
              :disabled="searching_imei"
            />
            <b-input-group-append>
              <b-btn
                variant="info"
                :disabled="imeiSearchDisabled"
                @click="searchByImei"
              >
                <v-icon v-if="searching_imei" name="spinner" spin></v-icon>
                <v-icon v-else name="search"></v-icon>
              </b-btn>
            </b-input-group-append>
          </b-input-group>
        </b-col>
      </b-row>
      <!-- Result devices table -->
      <b-row>
        <b-col>
          <b-table
            small
            primary-key="id"
            current-page="1"
            per-page="5"
            :items="resultDevices"
            :fields="fields"
          >
            <template v-slot:cell(imei)="row">
              <router-link
                style="color:#17a2b8"
                :to="{name: 'device', params:{ id: row.item.id }}"
              >
                {{ row.item.imei }}
              </router-link>
            </template>
            <template v-slot:cell(id)="row">
              <b-btn
                v-if="row.item.status=='stock'"
                size="sm"
                @click="addSelectedDevice(row.item)"
              >
                <v-icon name="plus"></v-icon>
              </b-btn>
            </template>
          </b-table>
        </b-col>
      </b-row>
      <!-- Selected devices table -->
      <b-row>
        <b-col>
          <b-table
            small
            primary-key="id"
            :current-page="currentPageSelected"
            :per-page="perPageSelected"
            :items="selectedDevices"
            :fields="fields"
            caption-top
          >
            <template v-slot:table-caption>
              {{ $t("globals.selected_devices") + ": " + noSelectedDevices }}
            </template>
            <template v-slot:cell(imei)="row">
              <router-link
                style="color:#17a2b8"
                :to="{name: 'device', params:{ id: row.item.id }}"
              >
                {{ row.item.imei }}
              </router-link>
            </template>
            <template v-slot:cell(id)="row">
              <b-btn
                size="sm"
                @click="removeSelectedDevice(row.item.id)"
              >
                <v-icon name="minus"></v-icon>
              </b-btn>
            </template>      
          </b-table>
          <b-pagination
            size="sm"
            :total-rows="selectedDevices.length"
            :per-page="perPageSelected"
            v-model="currentPageSelected"
          />
        </b-col>
      </b-row>
      <!-- Add Sold Event -->
      <b-row>
        <b-col>
          <b-form-checkbox
            class="m-1"
            v-model="sold"
            :value="true"
            :unchecked-value="false"
            :state="sold ? sold : null"
          >
            {{ $t("globals.add_a_sold_event_for_these_devices") }}
          </b-form-checkbox>
        </b-col>
      </b-row>
      <b-row v-if="sold">
         <b-col cols="12">
          <b-form-checkbox
            class="m-1"
            v-model="sold_lease"
            :value="true"
            :unchecked-value="false"
          >
            {{ $t("globals.lease") }}
          </b-form-checkbox>
        </b-col>
        <b-col cols="12">
          <span>{{ $t("globals.sold_at") }}:</span>
          <b-form-datepicker 
            :state="true"
            v-model="sold_at"
          ></b-form-datepicker>
        </b-col>
        <b-col
          v-if="getUserBrandPermissions.create_device_event_custom_agent"
          cols="12"
        >
          <span v-b-tooltip.hover title="Optional, if left empty, you will be assigned" >
            {{ "*" + $t("globals.select_agent") }}:
          </span>
          <b-form-select
            class="m-1"
            v-model="new_agent_id"
            :options="agentOptions"
          />
        </b-col>
      </b-row>
      <hr />
      <b-row>
        <b-col>
          <b-btn
            class="float-right m-1"
            @click="submit"
            variant="success"
            :disabled="submitDisabled"
          >
            {{ $t("globals.submit") }}
          </b-btn>
          <b-btn
            class="m-1"
            @click="cancelModal"
            variant="warning"
          >
            {{ $t("globals.cancel") }}
          </b-btn>
        </b-col>
      </b-row>
    </b-container>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { currencyFormat } from "@/helpers/price";

export default {
  props: {
    show: Boolean,
    close: Function
  },
  data() {
    return {
      sold: true,
      sold_lease: false,
      sold_at: "",
      search_imei: "",
      selectedDevices: [],
      resultDevices: [],
      perPageSelected: 5,
      currentPageSelected: 1,
      searching_imei: false,
      new_agent_id: null,
      agentOptions: [],
      fields: [
        {
          key: "id",
          label: "ID"
        },
        {
          key: "imei",
          label: "IMEI"
        },
        {
          key: "status",
          label: this.$t("globals.status")
        },
        {
          key: "product",
          label: this.$t("globals.product")
        },
        {
          key: "batch",
          label: this.$t("globals.batch")
        }
      ]
    };
  },
  methods: {
    ...mapActions("axios_mod", {
      simpleMutation: "simpleMutation",
      simpleFetch: "simpleFetch",
      fetchAgentsFromStore: "fetchAgents"
    }),
    currencyFormat,
    submit() {
      //const device_list = this.stockDevices.filter(e => e.selected).map(a => a.imei);
      const device_list = this.selectedDevices.map(a => a.imei);

      let data = {
          query: `
            mutation DevicesAssignToOrder (
              $device_list: [String],
              $order_id: Int,
              $sold: Boolean,
              $sold_lease: Boolean,
              $sold_at: String,
              $agent_id: ID
            ) { 
              res: DevicesAssignToOrder (
                device_list: $device_list,
                order_id: $order_id,
                sold: $sold,
                sold_lease: $sold_lease,
                sold_at: $sold_at,
                agent_id: $agent_id
              )
            }
          `,
          variables: {
            device_list: device_list,
            order_id: parseInt(this.$route.params.id),
            sold: this.sold,
            sold_lease: this.sold_lease,
            sold_at: this.sold_at,
            agent_id: this.new_agent_id
          }
        };

        this.simpleMutation({ data, this_ref: this }).then(() => {
          this.cancelModal();
        });
    },
    searchByImei() {
      if (!this.imeiSearchDisabled) {
        const data = {
          query: `
            query  {
              DeviceLikeImei(
                imei: "${this.search_imei}"
                limit: 5
              ) {
                id
                status
                product
                imei
                batch
              }
            }
          `
        };

        this.searching_imei = true;
        this.simpleFetch({data, this_ref: this}).then((res) => {
          if (res) {
            this.resultDevices = res.DeviceLikeImei
          }
        }).finally(()=>{
          this.searching_imei = false;
        });
      }
    },
    addSelectedDevice(dev) {
      // Check if is already in selected
      if (this.selectedDevices.filter(d => d.id == dev.id).length == 0) {
        // Remove from results
        this.resultDevices = this.resultDevices.filter(device => device.id !== dev.id);
        // Add to selected
        this.selectedDevices.push(dev);
      }
    },
    removeSelectedDevice(id) {
      this.selectedDevices = this.selectedDevices.filter(device => device.id !== id);
    },
    loadAgents() {
      this.fetchAgentsFromStore({ this_ref: this }).then(res => {
        if (res) {
          this.agentOptions = res;
        }
      });
    },
    cancelModal() {
      this.selectedDevices = [];
      this.resultDevices = [];
      this.search_imei = "";
      this.sold = false;
      this.sold_lease = false;
      this.new_agent_id = null;

      this.setToday();
      this.close();
    },
    setToday() {
      const now = new Date;
      this.sold_at = now.toISOString().substring(0,10);
    }
  },
  computed: {
    ...mapGetters(["getUserBrandPermissions"]),
    noSelectedDevices() {
      return this.selectedDevices.length
    },
    submitDisabled() {
      return this.noSelectedDevices == 0
    },
    imeiSearchDisabled() {
      return (
        this.search_imei==null
        || this.search_imei == ''
        || this.search_imei.length < 4
        || this.searching_imei
      )
    },
  },
  mounted() {
    this.setToday();
    this.loadAgents();
  }
};
</script>
